import { useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { TimeInput } from './TimeInput';
import { StartsAtStopsAt } from './StartsAtStopsAt';
import { useLockBodyScroll } from './useLockBodyScroll';

const animatedComponents = makeAnimated();

function timeStringToMinutes(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

export default function PrescriptionModal({
  event,
  langUrl,
  isOpen,
  onClose,
  onSave,
  onDelete,
  demoMode = false,
  trads,
  calendarTimezone,
}) {
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [everyDays, setEveryDays] = useState(2);
  const [timeSlots, setTimeSlots] = useState([undefined]);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [previewOccurrences, setPreviewOccurrences] = useState([]);

  const [startsAt, setStartsAt] = useState(null);
  const [stopsAt, setStopsAt] = useState(null);

  const t = trads?.index?.eventModal || {};
  const tIndex = trads?.index || {};
  const tContacts = trads?.contacts || {};

  useLockBodyScroll(isOpen);

  useEffect(() => {
    if (event) {
      setName(event.name || '');
      setContent(event.content || '');
      setEveryDays(event.everyDays || 2);
      setStartsAt(event.startsAt || null);
      setStopsAt(event.stopsAt || null);
      setSelectedGroupIds(event.groupIds || []);
      // Initialize time slots from event.times array
      if (event.times && event.times.length > 0) {
        setTimeSlots(event.times.map(() => undefined));
      } else {
        setTimeSlots([undefined]);
      }
      const selected = (event.eventContacts || []).map((ec) => ({
        value: ec.contactId || ec.contact?.id,
        label: ec.contact?.name || ec.name,
      }));
      setSelectedContacts(selected);
    }
  }, [event]);

  useEffect(() => {
    if (isOpen && !demoMode) {
      setLoadingContacts(true);
      fetch('/api/contacts/get-contacts')
        .then((res) => res.json())
        .then((data) => {
          if (data.success && data.contacts) {
            setContacts(data.contacts);
            setGroups(data.groups || []);
            if (data.contacts.length === 1) {
              setSelectedContacts((prev) =>
                prev.length === 0
                  ? [
                      {
                        value: data.contacts[0].id,
                        label: data.contacts[0].name,
                      },
                    ]
                  : prev
              );
            }
          }
        })
        .catch((err) => console.error('Failed to fetch contacts:', err))
        .finally(() => setLoadingContacts(false));
    }
  }, [isOpen, demoMode]);

  // Preview occurrences
  const fetchPreview = useCallback(async () => {
    const validTimes = timeSlots
      .map((ts, i) => {
        if (ts) return timeStringToMinutes(ts);
        // Fallback to event.times[i] if user hasn't changed it
        if (event?.times?.[i] != null) return event.times[i];
        return null;
      })
      .filter((t) => t != null);

    if (validTimes.length === 0 || everyDays < 1) {
      setPreviewOccurrences([]);
      return;
    }

    try {
      const res = await fetch('/api/preview-occurrences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timezone: calendarTimezone || 'Europe/Paris',
          times: validTimes,
          everyDays,
          startsAt: startsAt || undefined,
          stopsAt: stopsAt || undefined,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setPreviewOccurrences(data.occurrences || []);
      }
    } catch (err) {
      console.error('Failed to fetch preview:', err);
    }
  }, [timeSlots, everyDays, startsAt, stopsAt, calendarTimezone, event]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(fetchPreview, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, fetchPreview]);

  const contactOptions = contacts.map((c) => ({ value: c.id, label: c.name }));
  const groupOptions = groups.map((group) => ({
    value: group.id,
    label: `${group.name} (${group.count})`,
  }));
  const selectedGroupOptions = groupOptions.filter((group) =>
    selectedGroupIds.includes(group.value)
  );

  if (!isOpen || !event) return null;

  const handleTimeChange = (index, value) => {
    setTimeSlots((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const addTimeSlot = () => {
    setTimeSlots((prev) => [...prev, undefined]);
  };

  const removeTimeSlot = (index) => {
    setTimeSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    const validTimes = timeSlots
      .map((ts, i) => {
        if (ts) return timeStringToMinutes(ts);
        if (event?.times?.[i] != null) return event.times[i];
        return null;
      })
      .filter((t) => t != null);

    onSave?.({
      ...event,
      name,
      content,
      startsAt,
      stopsAt,
      everyDays,
      days: [],
      daysInMonth: [],
      times: validTimes.length > 0 ? validTimes : [540],
      eventContacts: selectedContacts.map((sc) => ({ contactId: sc.value })),
      groupIds: selectedGroupIds,
    });
    onClose();
  };

  const handleDelete = () => {
    if (onDelete && event.id) {
      onDelete(event.id);
      onClose();
    }
  };

  const formatOccurrence = (isoStr) => {
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: calendarTimezone || 'Europe/Paris',
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {tIndex.prescriptionTitle || 'Reminder sequences'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <input
              type="text"
              value={name}
              placeholder={t.name || 'Event name'}
              onChange={(e) => setName(e.target.value)}
              data-testid="prescription-name-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* SMS Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.smsContent || 'SMS text'}
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              data-testid="prescription-content-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          {/* Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {tIndex.frequency || 'Frequency'}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-gray-700">{tIndex.every || 'Every'}</span>
              {everyDays > 1 && (
                <input
                  type="number"
                  min={1}
                  value={everyDays}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!isNaN(v) && v >= 1) setEveryDays(v);
                  }}
                  data-testid="prescription-frequency-input"
                  className="w-20 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-center"
                />
              )}
              <span className="text-gray-700">
                {everyDays === 1
                  ? tIndex.dayLabelSingular || 'day'
                  : tIndex.daysLabel || 'days'}
              </span>
              {everyDays === 1 && (
                <button
                  type="button"
                  onClick={() => setEveryDays(2)}
                  data-testid="prescription-frequency-input"
                  className="text-sm text-gray-600 hover:text-gray-800 underline font-medium"
                >
                  {tIndex.changeFrequency || '(change)'}
                </button>
              )}
            </div>
          </div>

          {/* Time slots */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.time || 'Time'}
            </label>
            {timeSlots.map((_, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <TimeInput
                  trads={trads}
                  onChange={(val) => handleTimeChange(index, val)}
                  defaultValue={event?.times?.[index]}
                  hideFormatToggle={index > 0}
                />
                {timeSlots.length > 1 && index === timeSlots.length - 1 && (
                  <button
                    type="button"
                    onClick={() => removeTimeSlot(index)}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                  >
                    {tIndex.remove || 'remove'}
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addTimeSlot}
              data-testid="prescription-add-time-btn"
              className="text-sm text-gray-600 hover:text-gray-800 underline font-medium"
            >
              {tIndex.addAnotherTime || '+ Add another time'}
            </button>
          </div>

          <StartsAtStopsAt
            startsAt={startsAt}
            stopsAt={stopsAt}
            setStartsAt={setStartsAt}
            setStopsAt={setStopsAt}
            trads={trads}
          />

          {/* Contacts */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
              {t.contacts || 'Contacts'}
            </label>
            {demoMode ? (
              <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-500 text-sm">
                No contacts selected
              </div>
            ) : (
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={contactOptions}
                value={selectedContacts}
                onChange={setSelectedContacts}
                isLoading={loadingContacts}
                placeholder={t.selectContacts || 'Select contacts...'}
                noOptionsMessage={() => tContacts.noContacts || 'No contacts'}
                classNamePrefix="contacts-select"
              />
            )}
            {contactOptions.length === 0 && (
              <div className="py-2 text-sm">
                <a
                  className="font-medium text-gray-900 underline hover:text-gray-700"
                  href={`/${langUrl}/contacts`}
                >
                  {t.addContacts}
                </a>
              </div>
            )}
          </div>

          {!demoMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 mt-2">
                {t.groups || 'Groups'}
              </label>
              <Select
                closeMenuOnSelect={false}
                components={animatedComponents}
                isMulti
                options={groupOptions}
                value={selectedGroupOptions}
                onChange={(values) =>
                  setSelectedGroupIds(
                    (values || []).map((group) => group.value)
                  )
                }
                placeholder={t.selectGroups || 'Select groups...'}
                noOptionsMessage={() => tContacts.noGroups || 'No groups'}
                data-testid="prescription-groups-select"
                classNamePrefix="contacts-select"
              />
            </div>
          )}

          {/* Incoming events preview */}
          {previewOccurrences.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {tIndex.incomingEvents || 'Incoming events'}
              </label>
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3 space-y-1">
                {previewOccurrences.map((occ, i) => (
                  <div key={i} className="text-sm text-gray-700">
                    {formatOccurrence(occ)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="mt-6 flex justify-between gap-3">
          {onDelete && event.id ? (
            <button
              type="button"
              onClick={handleDelete}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
            >
              {tContacts.delete || 'Delete'}
            </button>
          ) : (
            <div></div>
          )}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
            >
              {t.cancel || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={
                content.trim().length < 3 ||
                everyDays < 1 ||
                timeSlots.every((ts, i) => !ts && event?.times?.[i] == null)
              }
              data-testid="prescription-save-btn"
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.save || 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
